import { ColumnType } from 'antd/es/table';
import { getCalendarDateTime } from '@sonar-software/sonar-ts-lib';
import { OrganizationMetadata } from '@sonar-software/sonar-core';
import React from 'react';

import {
	ADMIN_DASHBOARD_COPY,
	ORGANIZATIONS_TABLE_DATA_INDICES,
	ORGANIZATIONS_TABLE_KEYS,
	SETTINGS_ORGANIZATION_COPY
} from '@sonar-software/sonar-ts-constants';
import { CloseCircleOutlined, CloudSyncOutlined } from '@ant-design/icons';
import { DisconnectButton, OrganizationNameInput, PackageTagSwitch, ReauthenticationButton } from './columns';
import { Space, Tag } from 'antd';

type ColumnDefinition = ColumnType<OrganizationMetadata>;

const sandboxTag =
	<Tag
		className='org-type-tag'
		color='volcano'>
		{ SETTINGS_ORGANIZATION_COPY.SANDBOX }
	</Tag>;
const productionTag =
	<Tag
		className='org-type-tag'
		color='geekblue'>
		{ SETTINGS_ORGANIZATION_COPY.PRODUCTION }
	</Tag>;
function getOrganizationNameColumn(): ColumnDefinition {
	return {
		fixed: 'left',
		key: ORGANIZATIONS_TABLE_KEYS.ORGANIZATION_NAME,
		render: (_, organization) =>
			<>
				<OrganizationNameInput
					organizationId={ organization.id }
					organizationName={ organization.name } />
				{ organization.isSandbox ? sandboxTag : productionTag }
			</>,
		title: SETTINGS_ORGANIZATION_COPY.ORGANIZATION_NAME
	};
}

function getOrganizationInstanceUrlColumn(): ColumnDefinition {
	return {
		dataIndex: ORGANIZATIONS_TABLE_DATA_INDICES.INSTANCE_URL,
		title: SETTINGS_ORGANIZATION_COPY.INSTANCE_URL
	};
}

function getOrganizationSyncStatusColumn(): ColumnDefinition {
	return {
		key: ORGANIZATIONS_TABLE_KEYS.SYNC_STATUS,
		render: (_, organization) => {
			if (organization.salesforceSyncInProgress) {
				return (
					<Space>
						<CloudSyncOutlined className='syncing-icon' />
						{ ADMIN_DASHBOARD_COPY.SYNCING }
					</Space>
				);
			} else if (organization.salesforceSyncFailed) {
				return (
					<Space>
						<CloseCircleOutlined className='failed-icon' />
						{ ADMIN_DASHBOARD_COPY.FAILED }
					</Space>
				);
			}

			return null;
		},
		title: SETTINGS_ORGANIZATION_COPY.SYNC_STATUS
	};
}

function getOrganizationSalesforceUsernameColumn(): ColumnDefinition {
	return {
		dataIndex: ORGANIZATIONS_TABLE_DATA_INDICES.SALESFORCE_USERNAME,
		title: SETTINGS_ORGANIZATION_COPY.SALESFORCE_USERNAME
	};
}

function getOrganizationLastSyncColumn(): ColumnDefinition {
	return {
		key: ORGANIZATIONS_TABLE_KEYS.LAST_SYNC,
		render: (_, organization) => {
			const lastSyncTime = organization.latestIndexTime;

			if (!lastSyncTime) {
				return null;
			}

			return getCalendarDateTime(lastSyncTime);
		},
		title: SETTINGS_ORGANIZATION_COPY.LAST_SUCCESSFUL_SYNC
	};
}

function getOrganizationDisconnectColumn(): ColumnDefinition {
	return {
		key: ORGANIZATIONS_TABLE_KEYS.DISCONNECT,
		render: (_, organization) => <DisconnectButton organization={ organization } />
	};
}

function getOrganizationReauthorizeColumn(): ColumnDefinition {
	return {
		key: ORGANIZATIONS_TABLE_KEYS.REAUTHORIZE,
		render: (_, organization) => <ReauthenticationButton organization={ organization } />
	};
}

function getOrganizationPackageTaggingColumn(isUserReadonly: boolean): ColumnDefinition {
	return {
		key: ORGANIZATIONS_TABLE_KEYS.PACKAGE_TAGGING,
		render: (_, organization) =>
			<PackageTagSwitch
				disabled={ isUserReadonly }
				organizationId={ organization.id }
				packageTagEnabled={ organization.packageTagEnabled } />,
		title: SETTINGS_ORGANIZATION_COPY.PACKAGE_TAGGING
	};
}

export function getOrganizationTableColumns(isUserReadonly: boolean): Array<ColumnDefinition> {
	const columns = [
		getOrganizationNameColumn(),
		getOrganizationInstanceUrlColumn(),
		getOrganizationSyncStatusColumn(),
		getOrganizationSalesforceUsernameColumn(),
		getOrganizationLastSyncColumn(),
		getOrganizationReauthorizeColumn(),
		getOrganizationDisconnectColumn(),
		getOrganizationPackageTaggingColumn(isUserReadonly)
	];

	if (isUserReadonly) {
		columns.splice(4, 2);
	}

	return columns;
}
